/* global ApplePaySession */
import applePay from 'braintree-web/apple-pay';
import dataCollector from 'braintree-web/data-collector';
import { upperCase } from 'lodash';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo, useContext } from 'react';
import cookies from 'react-cookies';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_IMG_APPLE_PAY } from '../config/constants/images';
import { PAYMENT_METHODS, PAYMENT_TYPES } from '../config/constants/payment';
import COOKIES from '../config/cookies/cookies';
import { LINK_SECOND_CHECKOUT } from '../config/links/links';
import {
  APPLE_BUTTON_IMG_TEXT,
  BUTTON_APPLE,
  ERROR_MESSAGE_BRAINTREE,
  GET_IT_BY,
  OUT_OF_STOCK_TEXT,
  PAYMENT_FAILED_TEXT,
  USER_PURCHASE_CAP_TEXT,
} from '../config/text/text';
import { trackEvent } from '../helpers/analytics';
import { getExpressBuyToken } from '../helpers/api';
import BraintreeService from '../helpers/braintreeService';
import { setCookie } from '../helpers/cookieSetter';
import CustomErrorsService from '../helpers/customErrorsService';
import { formatDeliveryDate } from '../helpers/date';
import ErrorTrackingService from '../helpers/errorTrackingService';
import travelService from '../helpers/travelService';
import { redirectToURL } from '../helpers/url';
import { getUserStatus } from '../helpers/userStatus';
import wait from '../helpers/wait';
import PurchaseContext from '../providers/PurchaseProvider';
import { setCheckoutStatus } from '../redux/actions/checkout';
import { registerUser } from '../redux/actions/user';
import { CHECKOUT_STATUS } from '../redux/reducers/checkout';
import {
  fetchExpressCheckoutDealData,
  getTotalQuantityForDealItems,
} from '../services/dealGoogleExpressCheckout';
import { logToSplunk } from './_generic/axiosSplunk/axiosSplunk';
import CTAButton from './_generic/button/CTAButton';
import Spinner from './_generic/spinner/Spinner';
import useToast from './_generic/toast/UseToast';

const ApplePayDealExpresssCheckout = ({
  afterError,
  basketProducts,
  finalOrderAmount,
  totalQuantity,
  userEmail,
  providerError,
  customError,
  isPostCheckout,
  isMobile = false,
  oneClickCheckout,
  selectedOptions,
  position,
  isOptionSelected,
  selection,
  deal,
  locationStore,
  isAuthenticated,
  isExpressDealPage = false,
  noBottomText = false,
  deposit = false,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [instance, setInstance] = useState(null);
  const [products, setProducts] = useState([]);
  const [eligibilityStatus, setEligibilityStatus] = useState(null);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [qty, setQty] = useState(totalQuantity);
  const [orderSummary, setOrderSummary] = useState();
  const [isTravel] = useSelector((state) => [state?.checkout?.isTravel]);
  const [, setOrderId] = useContext(PurchaseContext);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);
  const locationCookie = cookies?.load(COOKIES?.location);
  const countryCode = process.env.NEXT_PUBLIC_COUNTRY_CODE;
  const isWalletApplied = false;
  const [locations] = useSelector((state) => [state.locations.locations]);
  const locationList = useMemo(() => {
    return locations
      .filter((loc) => loc.countryCode === countryCode)
      .map((loc) => ({
        ...loc,
        lowerName: loc.name?.toLowerCase(),
        lowerShortName: loc.shortName?.toLowerCase(),
      }));
  }, [locations, countryCode]);

  const getValidLocation = (locality) => {
    const lowerCaseLocality = locality.toLowerCase();
    const res = locationList.find(
      (item) =>
        item.lowerShortName === lowerCaseLocality ||
        item.lowerName === lowerCaseLocality,
    );

    return res?.shortName;
  };

  const toast = useToast();

  useEffect(() => {
    const fetchAndSetData = async () => {
      try {
        const response = await fetchExpressCheckoutDealData(
          selectedOptions,
          position,
          isOptionSelected,
          selection,
          deal,
          locationStore,
          deposit,
        );
        setOrderSummary(response?.orderSummary);
        setEligibilityStatus(response?.dealPurchaseEligibilityStatus);
        if (
          response?.orderSummary?.vipSummary?.basketDiscountForExistingVipUser
        ) {
          BraintreeService.basketDiscountForExistingVipUser =
            response?.orderSummary?.vipSummary?.basketDiscountForExistingVipUser;
        }
        if (response?.deals?.length > 0) {
          setProducts(response?.deals[0]?.products);
        }

        if (response?.deliveryOptions?.length > 0) {
          setDeliveryOptions(response?.deliveryOptions);
        }
        const quantityForDealPage = getTotalQuantityForDealItems(
          response?.dealProducts,
        );
        setQty(quantityForDealPage);
      } catch (error) {
        console.error('Error fetching deal order summary:', error);
      }
    };

    fetchAndSetData();
  }, [selectedOptions, position, isOptionSelected, selection, deposit]);

  const toggleLoaderOff = () => {
    setIsLoading(false);
  };
  const toggleLoaderOn = () => {
    setIsLoading(true);
  };

  const isApplePayAvailable = () => {
    return window.ApplePaySession && window.ApplePaySession.canMakePayments();
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const setupMethods = async (clientInstance) => {
    if (!clientInstance) return;
    const emailUsed = cookies.load(COOKIES.expressCheckoutEmail);
    try {
      const response = await dataCollector.create({
        client: clientInstance,
        kount: true,
        paypal: true,
      });

      if (response) {
        setDataCollectorInstance(response);
        BraintreeService.deviceData = response.deviceData;
      }

      createApplePayInstance(clientInstance);
    } catch (error) {
      ErrorTrackingService.logError(error);
      toggleLoaderOff();
      logToSplunk(
        {
          component: 'appleExpressCheckout',
          email: emailUsed,
          error,
          type: 'Deal apple express checkout 1',
        },
        'frontend logs',
      );
    }
  };

  const createApplePayInstance = async (clientInstance) => {
    const emailUsed = cookies.load(COOKIES.expressCheckoutEmail);
    if (!clientInstance) return;
    try {
      const applePayInstance = await applePay.create({
        client: clientInstance,
      });
      setInstance(applePayInstance);
      toggleLoaderOff();
    } catch (error) {
      console.error(`Error creating Apple Pay Instance: ${error}`);
      notifyError(ERROR_MESSAGE_BRAINTREE);
      trackEvent('applepay_aborted');
      toggleLoaderOff();
      logToSplunk(
        {
          component: 'appleExpressCheckout',
          email: emailUsed,
          error,
          type: 'apple express checkout 2',
        },
        'frontend logs',
      );
    }
  };

  const handleOrderComplete = (order) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    toggleLoaderOff();
    trackEvent('d_express_checkout_payment_completed');
    // call order complete in payment component
    redirectToSecondCheckout(order);
  };

  const redirectToSecondCheckout = async (order) => {
    toast.addToast('Payment successfull', 'toast-success');

    if (isTravel) {
      try {
        await travelService.instantRedeem(order?.data?.productOrder?.id);
      } catch (error) {
        console.error(error);
      }
    }
    setOrderId(order?.data?.productOrder?.id);

    await wait(2_000);
    const orderIdFromAPI =
      order?.data?.productOrder?.id || order?.productOrder?.id;

    redirectToURL(`${LINK_SECOND_CHECKOUT}/benefits/${orderIdFromAPI}`, router);
  };
  const handleOrderError = (error) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }

    if (
      error.data &&
      error.data.errors &&
      Object.keys(error.data.errors).length > 0
    ) {
      // custom errors
      const customErrors = CustomErrorsService.parseErrors(
        error.data.errors,
        error.config.data || {},
      );
      customError({ errors: customErrors });
    } else {
      // default error message
      notifyError(PAYMENT_FAILED_TEXT);
    }

    toggleLoaderOff();
    trackEvent('d_express_checkout_failed');
    // setPaymentInProcess(false);

    // will reload the checkout
    afterError();
  };

  const setupBraintree = async () => {
    BraintreeService.paymentComplete = handleOrderComplete;
    BraintreeService.paymentError = handleOrderError;
    BraintreeService.paymentType = PAYMENT_TYPES.apple;
    BraintreeService.totalQuantity = qty;
    BraintreeService.providerError = providerError;
    const isLoggedOutUser = !isAuthenticated;
    const clientInstance = await BraintreeService.getClientInstance(
      isLoggedOutUser,
    );
    if (clientInstance) {
      setupMethods(clientInstance);
    }
  };

  useEffect(() => {
    setupBraintree();
  }, [qty]);

  const submitPayment = (credit) => {
    if (eligibilityStatus === 'SOLD_OUT') {
      notifyError(OUT_OF_STOCK_TEXT);

      return;
    } else if (eligibilityStatus === 'USER_CAP_REACHED') {
      notifyError(USER_PURCHASE_CAP_TEXT);

      return;
    } else if (eligibilityStatus === 'EXPIRED_OR_SPIKED') {
      notifyError(PAYMENT_FAILED_TEXT);

      return;
    }
    BraintreeService.totalAmountToPay = orderSummary?.finalOrderAmount;
    trackEvent('d_express_checkout');
    toggleLoaderOn();
    if (!isApplePayAvailable()) {
      alert('Apple Pay is not available on this device');

      return;
    }

    let shippingMethods;
    if (deliveryOptions?.length > 0) {
      shippingMethods = deliveryOptions.map((option, index) => ({
        amount: option.customerPostageCost.toFixed(2),
        detail: `Redeem today and ${GET_IT_BY} ${formatDeliveryDate(
          option?.estimatedDeliveryDate,
        )}`,
        identifier: option.deliveryOptionType,
        label: option.deliveryOptionName,
      }));
    }
    const paymentRequest = {
      countryCode: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE),
      currencyCode: upperCase(orderSummary?.currency),
      merchantCapabilities: ['supports3DS'],
      requiredBillingContactFields: ['postalAddress', 'name', 'email', 'phone'],
      requiredShippingContactFields: ['email'],
      supportedNetworks: ['visa', 'masterCard'],
      total: {
        amount: orderSummary?.finalOrderAmount?.toFixed(2).toString(),
        label: process.env.NEXT_PUBLIC_BRAND,
      },
    };
    if (orderSummary?.totalAdminFee > 0) {
      paymentRequest.lineItems = [
        {
          amount: orderSummary?.totalOrderAmount?.toFixed(2).toString(),
          label: 'Subtotal',
        },
        {
          amount: orderSummary?.totalAdminFee?.toFixed(2).toString(),
          label: 'Administration Fee',
        },
      ];
    }

    if (
      orderSummary?.vipSummary?.basketDiscountForExistingVipUser > 0 &&
      orderSummary?.totalAdminFee > 0
    ) {
      paymentRequest.lineItems.push({
        amount: `-${orderSummary?.vipSummary?.basketDiscountForExistingVipUser
          ?.toFixed(2)
          .toString()}`,
        label: 'VIP Discount',
      });
    }
    if (
      orderSummary?.vipSummary?.basketDiscountForExistingVipUser > 0 &&
      orderSummary?.totalAdminFee === 0
    ) {
      paymentRequest.lineItems = [
        {
          amount: orderSummary?.totalOrderAmount?.toFixed(2).toString(),
          label: 'Subtotal',
        },
        {
          amount: `-${orderSummary?.vipSummary?.basketDiscountForExistingVipUser
            ?.toFixed(2)
            .toString()}`,
          label: 'VIP Discount',
        },
      ];
    }

    if (shippingMethods) {
      paymentRequest.shippingMethods = shippingMethods;
      if (paymentRequest?.lineItems) {
        paymentRequest.lineItems.push({
          amount: shippingMethods[0]?.amount,
          label: 'Post & Packaging',
        });
      } else {
        paymentRequest.lineItems = [
          {
            amount: shippingMethods[0]?.amount,
            label: 'Post & Packaging',
          },
        ];
      }
    }
    const session = new ApplePaySession(3, paymentRequest);
    const emailUsed = cookies.load(COOKIES.expressCheckoutEmail);
    session.onvalidatemerchant = async (event) => {
      try {
        const merchantSession = await instance?.performValidation({
          displayName: process.env.NEXT_PUBLIC_BRAND,
          validationURL: event.validationURL,
        });
        if (merchantSession) {
          session.completeMerchantValidation(merchantSession);
        }
      } catch (error) {
        // You should show an error to the user, e.g. 'Apple Pay failed to load.'
        console.error('Error validating merchant:', error);
        notifyError(PAYMENT_FAILED_TEXT);
        session.abort();
        toggleLoaderOff();
        logToSplunk(
          {
            component: 'appleExpressCheckout',
            email: emailUsed,
            error,
            type: 'Deal apple express checkout 3',
          },
          'frontend logs',
        );
      }
    };

    let basketProducts = products;
    if (deliveryOptions?.length > 0) {
      session.onshippingmethodselected = function (event) {
        // Update the total amount based on the selected shipping method
        const selectedShippingMethod = shippingMethods.find(
          (method) => method.label === event.shippingMethod.label,
        );

        if (selectedShippingMethod) {
          // Update each product with the selected deliveryOptionType
          const updatedProducts = products.map((product) => ({
            ...product,
            deliveryOptionType: selectedShippingMethod.identifier,
          }));
          setProducts(updatedProducts);
          basketProducts = updatedProducts;

          // Update the total amount only if the selected shipping method is not the default one
          let updatedTotal;
          if (orderSummary?.vipSummary?.basketDiscountForExistingVipUser > 0) {
            updatedTotal =
              Number.parseFloat(orderSummary?.totalOrderAmount) +
              Number.parseFloat(selectedShippingMethod.amount) -
              Number.parseFloat(
                orderSummary?.vipSummary?.basketDiscountForExistingVipUser,
              );
          } else {
            updatedTotal =
              Number.parseFloat(orderSummary?.totalOrderAmount) +
              Number.parseFloat(selectedShippingMethod.amount);
          }
          paymentRequest.total.amount = updatedTotal.toFixed(2);
          BraintreeService.totalAmountToPay = updatedTotal;

          const lineItems = paymentRequest.lineItems || [];
          const postageIndex = paymentRequest.lineItems.findIndex(
            (item) => item.label === 'Post & Packaging',
          );
          if (postageIndex !== -1) {
            lineItems[postageIndex].amount = selectedShippingMethod.amount;
          } else {
            lineItems.push({
              amount: selectedShippingMethod.amount,
              label: 'Post & Packaging',
            });
          }
          session.completeShippingMethodSelection(
            ApplePaySession.STATUS_SUCCESS,
            paymentRequest.total,
            lineItems,
          );
        }
      };
    }
    session.onpaymentauthorized = async (event) => {
      try {
        const retrievedEmail = event.payment.shippingContact.emailAddress;
        let emailUsed;
        if (isPostCheckout) {
          emailUsed = cookies.load(COOKIES.expressCheckoutEmail);
        } else {
          emailUsed = userEmail ? userEmail : retrievedEmail;
        }
        // set cookie to store this email, to be used on my-wowchers page
        setCookie({
          expire: 1,
          key: COOKIES.expressCheckoutEmail,
          value: emailUsed,
        });

        if (isAuthenticated) {
          try {
            const tokenizationPayload = await instance.tokenize({
              token: event.payment.token,
            });
            if (tokenizationPayload) {
              session.completePayment(ApplePaySession.STATUS_SUCCESS);
              const expressDealCheckout = true;
              BraintreeService.preparePayment({
                basketProducts,
                credit,
                dataCollectorInstance,
                expressDealCheckout,
                isWalletApplied,
                orderSummary,
                paymentMethod: PAYMENT_METHODS.applePay,
                tokenizationPayload,
              });
            }
          } catch (error) {
            // setPaymentInProcess(false);
            console.error('Error tokenizing Apple Pay:', error);
            notifyError(PAYMENT_FAILED_TEXT);
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            toggleLoaderOff();
          }
        } else {
          // Step 1: Get user status using email address from payment shipping contact
          const userStatusResponse = await getUserStatus(emailUsed);

          if (userStatusResponse) {
            let authResponse; // Variable to store response from register or login API
            if (!isPostCheckout) {
              if (userStatusResponse.isNewUser) {
                // Step 2a: If new user, call express register API
                trackEvent('d_express_checkout_guest_flow');
                const subscriptionCookie = cookies.load(COOKIES.userSource);

                const values = {
                  addressLine1: event.payment.billingContact?.addressLines[0],
                  city: event.payment.billingContact.locality,
                  email: emailUsed,
                  emailConfirmation: emailUsed,
                  emailOptOut: cookies.load(COOKIES.emailOptOut),
                  firstName: event.payment.billingContact.givenName,
                  location:
                    getValidLocation(
                      event?.payment?.billingContact?.locality,
                    ) || locationCookie?.name,
                  postCode: event.payment.billingContact.postalCode,
                  rejectDMGTContact: false,
                  subscriptionSource: subscriptionCookie
                    ? decodeURIComponent(subscriptionCookie)
                    : 'expressApplePay',
                  surname: event.payment.billingContact.familyName,
                  title: '',
                };

                // don't send autocomplete field to BE
                // eslint-disable-next-line fp/no-delete
                delete values.autocomplete;
                const isPasswordless = true;
                const data = await registerUser(values, isPasswordless);
                authResponse = data?.data?.apiAuthToken;
                setCookie({
                  expireInHours: 1,
                  key: COOKIES.expressRegister,
                  value: 'true',
                });
              } else {
                // Step 2b: If existing user, call login API (get express buy token)
                trackEvent('d_express_checkout_existing_user_flow');
                const data = await getExpressBuyToken(emailUsed);
                authResponse = data?.accessToken;
                localStorage.setItem(
                  COOKIES.expressAuthToken,
                  authResponse,
                  600_000,
                );
              }
            } else {
              authResponse = localStorage.getItem(COOKIES.expressAuthToken);
            }

            if (authResponse) {
              // Step 3: Call /token API to generate the token
              const AuthenticatedToken = await BraintreeService.getClientTokenWithExpressBuyToken(
                authResponse,
              );
              const expressBuyAuthToken = authResponse;

              const BTToken = AuthenticatedToken?.data?.clientToken;
              const tokenizationPayload = await instance.tokenize({
                token: event.payment.token,
              });
              // Step 4: Prepare Payment for Braintree
              if (tokenizationPayload) {
                session.completePayment(ApplePaySession.STATUS_SUCCESS);

                const expressCheckout = false;
                const basketProducts = products;
                const expressDealCheckout = true;
                if (userStatusResponse.isNewUser) {
                  BraintreeService.preparePayment({
                    basketProducts,
                    credit,
                    dataCollectorInstance,
                    expressCheckout,
                    expressDealCheckout,
                    isWalletApplied,
                    orderSummary,
                    paymentMethod: PAYMENT_METHODS.applePay,
                    tokenizationPayload,
                  });
                } else {
                  BraintreeService.preparePayment({
                    basketProducts,
                    credit,
                    dataCollectorInstance,
                    expressBuyAuthToken,
                    expressCheckout,
                    expressDealCheckout,
                    isWalletApplied,
                    orderSummary,
                    paymentMethod: PAYMENT_METHODS.applePay,
                    tokenizationPayload,
                  });
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error tokenizing Apple Pay:', error);
        notifyError(PAYMENT_FAILED_TEXT);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        toggleLoaderOff();
        logToSplunk(
          {
            component: 'appleExpressCheckout',
            email: emailUsed,
            error,
            type: 'Deal apple express checkout 4',
          },
          'frontend logs',
        );
      }
    };

    session.oncancel = () => {
      console.error('Payment cancelled');
      notifyError(`Payment aborted`);
      toggleLoaderOff();
      session.abort();
    };
    session.begin();
  };

  return (
    <div className="div-wrapper">
      {isLoading && <Spinner isFullScreen />}
      <div
        style={{
          alignItems: isPostCheckout ? 'center' : '',
          display: isPostCheckout ? 'flex' : '',
          justifyContent: isPostCheckout ? 'center' : '',
        }}
      >
        <CTAButton
          className="apple-pay-btn-deal-page full-width black-button"
          data-qa="payWithApplePay"
          isExpressDealPage={isExpressDealPage}
          onClick={() => submitPayment(false)}
        >
          <span className=".apple-pay-text">{BUTTON_APPLE}&nbsp;</span>
          <img
            alt={APPLE_BUTTON_IMG_TEXT}
            src={BUTTON_IMG_APPLE_PAY}
            style={{ width: '45px' }}
          />
        </CTAButton>
      </div>
      {!isMobile && !noBottomText && (
        <span className="apple-pay-bottom-text">
          {'Click above to Place Your Order'}
        </span>
      )}
      <style jsx>{`
        .div-wrapper {
          min-width: ${isExpressDealPage ? '24rem' : '28rem'};
          margin-left: ${isExpressDealPage ? '0px' : '10px'};
          margin-top: ${!isMobile && oneClickCheckout === 'b'
            ? '-15px'
            : '0px'};
        }
        .apple-pay-text {
          font-weight: 400;
        }
        .apple-pay-bottom-text {
          font-weight: bold;
          text-align: center;
          margin: 0 auto;
          font-size: 12px;
          display: block;
        }
      `}</style>
    </div>
  );
};

ApplePayDealExpresssCheckout.propTypes = {};

export default ApplePayDealExpresssCheckout;
